import React, { useState } from 'react';
import QuestionForm from './QuestionForm';
import QuestionImport from '../Common/QuestionImport';
import AIQuizGenerator from '../Common/AIQuizGenerator';
import { useAuth } from '../Auth/AuthProvider';
import './QuizForm.css';

const QuizForm = ({ onSave, onCancel }) => {
  const { user } = useAuth();
  const [quiz, setQuiz] = useState({
    title: '',
    description: '',
    visibility: 'private',
    published: false
  });
  const [publishMode, setPublishMode] = useState('private'); // 'private' | 'limited' | 'public'
  const [questions, setQuestions] = useState([]);
  const [editingQuestionIndex, setEditingQuestionIndex] = useState(null);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [showImportForm, setShowImportForm] = useState(false);
  const [showAiGenerator, setShowAiGenerator] = useState(false);
  const [aiGeneratedJson, setAiGeneratedJson] = useState('');
  const [loading, setLoading] = useState(false);

  const handleQuizChange = (field, value) => {
    setQuiz(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 公開モード変更時に quiz.visibility / quiz.published を同期
  const handlePublishModeChange = (mode) => {
    setPublishMode(mode);
    if (mode === 'private') {
      setQuiz(prev => ({ ...prev, visibility: 'private', published: false }));
    } else if (mode === 'limited') {
      // 限定公開: visibility = 'public', published = false
      setQuiz(prev => ({ ...prev, visibility: 'public', published: false }));
    } else if (mode === 'public') {
      setQuiz(prev => ({ ...prev, visibility: 'public', published: true }));
    }
  };

  const handleAddQuestion = () => {
    setEditingQuestionIndex(null);
    setShowQuestionForm(true);
  };

  const handleEditQuestion = (index) => {
    setEditingQuestionIndex(index);
    setShowQuestionForm(true);
  };

  const handleSaveQuestion = (questionData) => {
    if (editingQuestionIndex !== null) {
      // Edit existing question
      setQuestions(prev => prev.map((q, i) => 
        i === editingQuestionIndex ? questionData : q
      ));
    } else {
      // Add new question
      setQuestions(prev => [...prev, questionData]);
    }
    setShowQuestionForm(false);
    setEditingQuestionIndex(null);
  };

  const handleDeleteQuestion = (index) => {
    if (window.confirm('この質問を削除しますか？')) {
      setQuestions(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleImportQuestions = () => {
    setShowImportForm(true);
  };

  const handleAiQuizGeneration = () => {
    setShowAiGenerator(true);
  };

  const handleAiGenerateResult = (result) => {
    try {
      // Convert AI result to JSON string and show in import form
      const jsonString = JSON.stringify(result, null, 2);
      setAiGeneratedJson(jsonString);
      setShowAiGenerator(false);
      setShowImportForm(true);
    } catch (error) {
      console.error('Failed to process AI generated questions:', error);
      alert('AI生成されたクイズの処理に失敗しました');
    }
  };

  const handleImportSubmit = (questionsData) => {
    try {
      setQuestions(prev => [...prev, ...questionsData]);
      setShowImportForm(false);
      alert(`${questionsData.length}個の質問をインポートしました！`);
    } catch (error) {
      console.error('Failed to import questions:', error);
      alert('質問のインポートに失敗しました');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!quiz.title.trim()) {
      alert('クイズのタイトルを入力してください');
      return;
    }

    if (questions.length === 0) {
      alert('最低1つの質問を追加してください');
      return;
    }

    setLoading(true);
    try {
      const quizData = {
        ...quiz,
        questions: questions.map((q, index) => ({
          ...q,
          order: index + 1
        }))
      };
      
      await onSave(quizData);
    } catch (error) {
      console.error('クイズの保存に失敗しました:', error);
      alert('クイズの保存に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  if (showQuestionForm) {
    return (
      <QuestionForm
        question={editingQuestionIndex !== null ? questions[editingQuestionIndex] : null}
        onSave={handleSaveQuestion}
        onCancel={() => {
          setShowQuestionForm(false);
          setEditingQuestionIndex(null);
        }}
      />
    );
  }

  if (showImportForm) {
    return (
      <QuestionImport
        onImport={handleImportSubmit}
        onCancel={() => {
          setShowImportForm(false);
          setAiGeneratedJson('');
        }}
        initialJsonData={aiGeneratedJson}
      />
    );
  }

  if (showAiGenerator) {
    return (
      <AIQuizGenerator
        onGenerate={handleAiGenerateResult}
        onCancel={() => setShowAiGenerator(false)}
      />
    );
  }

  return (
    <div className="quiz-form">
      <div className="quiz-form-header">
        <h2>{editingQuestionIndex !== null ? '質問を編集' : '新しいクイズを作成'}</h2>
        <button onClick={onCancel} className="cancel-btn">
          ← 戻る
        </button>
      </div>

      <form onSubmit={handleSubmit} className="quiz-details-form">
        <div className="form-group">
          <label>クイズタイトル *</label>
          <input
            type="text"
            value={quiz.title}
            onChange={(e) => handleQuizChange('title', e.target.value)}
            placeholder="クイズのタイトルを入力"
            required
          />
        </div>

        <div className="form-group">
          <label>説明</label>
          <textarea
            value={quiz.description}
            onChange={(e) => handleQuizChange('description', e.target.value)}
            placeholder="クイズの説明を入力"
            rows="3"
          />
        </div>

        <div className="form-group">
          <label>公開設定</label>
          <select
            value={publishMode}
            onChange={(e) => handlePublishModeChange(e.target.value)}
          >
            <option value="private">非公開</option>
            <option value="limited">限定公開（URL を知っている人のみ）</option>
            <option value="public">一般公開（一覧に表示）</option>
          </select>
        </div>

        <div className="questions-section">
          <div className="questions-header">
            <h3>質問 ({questions.length})</h3>
            <div className="question-actions">
              <button 
                type="button" 
                onClick={handleAddQuestion}
                className="add-question-btn"
              >
                + 質問を追加
              </button>
              <button 
                type="button" 
                onClick={handleImportQuestions}
                className="import-question-btn"
              >
                📁 JSONからインポート
              </button>
              <button 
                type="button" 
                onClick={handleAiQuizGeneration}
                className="ai-generation-btn"
              >
                🤖 AIでクイズ作成
              </button>
            </div>
          </div>

          {questions.length === 0 ? (
            <div className="no-questions">
              <p>まだ質問が追加されていません</p>
              <p>「質問を追加」ボタンをクリックして最初の質問を作成してください</p>
            </div>
          ) : (
            <div className="questions-list">
              {questions.map((question, index) => (
                <div key={index} className="question-item">
                  <div className="question-header">
                    <span className="question-number">質問 {index + 1}</span>
                    <div className="question-actions">
                      <button
                        type="button"
                        onClick={() => handleEditQuestion(index)}
                        className="edit-btn"
                      >
                        編集
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteQuestion(index)}
                        className="delete-btn"
                      >
                        削除
                      </button>
                    </div>
                  </div>
                  <div className="question-preview">
                    <p><strong>質問:</strong> {question.questionText}</p>
                    <p><strong>タイプ:</strong> {question.questionType === 'single' ? '単一選択' : '複数選択'}</p>
                    <p><strong>選択肢:</strong> {question.choices.length}個</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="form-actions">
          <button type="button" onClick={onCancel} className="cancel-btn">
            キャンセル
          </button>
          <button 
            type="submit" 
            className="save-btn"
            disabled={loading}
          >
            {loading ? '保存中...' : 'クイズを保存'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default QuizForm;
